import { useContext } from 'react';
import { defineMessages } from 'react-intl';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import last from 'lodash/last';
import split from 'lodash/split';
import includes from 'lodash/includes';
import join from 'lodash/join';
import { ChatContext } from './chat-context';
import filter from 'lodash/filter';
import map from 'lodash/map';
import values from 'lodash/values';

export const useStreamChatContext = () => useContext(ChatContext);

// Converts bytes into formatted string:
// e.g. 7103 => 6.94 KB
export const formatBytes = (bytes, intl) => {
  if (!+bytes) {
    return intl.formatMessage({ id: 'stream.message_list.bytes', defaultMessage: '{value} B' }, { value: 0 });
  }
  const power = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = parseFloat((bytes / Math.pow(1024, power)).toFixed(2));
  const units = [
    intl.formatMessage({ id: 'stream.message_list.bytes', defaultMessage: '{value} B' }, { value }),
    intl.formatMessage({ id: 'stream.message_list.kbytes', defaultMessage: '{value} KB' }, { value }),
    intl.formatMessage({ id: 'stream.message_list.mbytes', defaultMessage: '{value} MB' }, { value })
  ];
  return units[power];
};

// Dynamically converts seconds into hh:mm:ss format
// e.g. 5.03 => 0:05
export const formatVideoTime = (secondsToFormat) => {
  const secondsNumber = parseInt(secondsToFormat, 10);
  let hours = Math.floor(secondsNumber / 3600);
  let minutes = Math.floor((secondsNumber - (hours * 3600)) / 60);
  let seconds = secondsNumber - (hours * 3600) - (minutes * 60);

  if (0 !== +minutes) {
    minutes = '0' + minutes;
  }
  if (10 > seconds) {
    seconds = '0' + seconds;
  }
  if (0 === +hours) {
    return `${minutes}:${seconds}`;
  }

  return `${hours}:${minutes}:${seconds}`;
};

export const noop = () => null;

export const useStreamChatErrors = () => {
  const { addInputErrorIds, clearInputErrors, inputErrors, removeInputErrorIds } = useContext(ChatContext);
  return { addInputErrorIds, clearInputErrors, inputErrors, removeInputErrorIds };
};

export const StreamErrorIds = {
  generic: 'generic',
  silent_error: 'silent_error', // Used when http response is a success but the message obj contains a type of 'error'
  invalid_file_size: 'invalid_file_size',
  invalid_image_size: 'invalid_image_size',
  invalid_file_extension: 'invalid_file_extension'
};

export const streamErrors = defineMessages({
  [StreamErrorIds.generic]: {
    id: 'stream.input-error.message',
    defaultMessage: 'Something went wrong, please try again.'
  },
  [StreamErrorIds.invalid_file_size]: {
    id: 'stream.input-error.attachments_file_size_error',
    defaultMessage: 'Files must be smaller than {size}.'
  },
  [StreamErrorIds.invalid_image_size]: {
    id: 'stream.input-error.attachments_image_size_error',
    defaultMessage: 'Images must be smaller than {size}.'
  },
  [StreamErrorIds.invalid_file_extension]: {
    id: 'stream.input-error.attachments_file_extension_error',
    defaultMessage: 'Invalid file extension.'
  }
});

export const getFileExtension = (file) => {
  const fileName = get(file, 'name', '');
  const extension = toLower(last(split(fileName, '.')));
  return extension ? `.${extension}` : null;
};

export const loadHeic2any = async () => {
  try {
    const heic2any = await import('heic2any');
    return heic2any.default;
  } catch (e) {
    console.log('Error when fetching a package', e);
    throw e;
  }
};

export const convertHEIF = async (file, convertType = 'jpeg') => {
  const heic2any = await loadHeic2any();
  const fileNameParts = split(file.name, '.');
  fileNameParts.pop();
  const fileName = join([...fileNameParts, convertType], '.');

  const convertedBlob = await heic2any({
    blob: file,
    toType: `image/${convertType}`,
    quality: 0.5
  });

  return new File([convertedBlob], fileName, { type: `image/${convertType}`, lastModified: file.lastModified });
};

export const isHEICOrHEIF = (file) => includes(['.heic', '.heif'], getFileExtension(file));

export const convertUploadsToAttachments = (imageUploads = {}, fileUploads = {}) => {
  const uploadsToArray = (uploads = {}) => {
    return filter(map(values(uploads), (upload) => {
      return upload && upload.file ? upload.file : null;
    }));
  };

  return [
    ...uploadsToArray(imageUploads),
    ...uploadsToArray(fileUploads)
  ];
}
